import { Component, Host, h, Prop, Watch, Element } from '@stencil/core';
import { DEFAULT_TRACKING_DELAY } from '../../global/utility/tracking-helpers';
import { CmsLink } from '../../types/global';

export interface UiCmsFaqEntryBody {
  faqId: string;
  requestUrl: string;
  url?: string;
  answer: string;
  question?: string;
  links?: CmsLink[];
  image?: string;
  imageAltText?: string;
  imageMouseover?: string;
  imageCopyright?: string;
  imageCaptionHeadline?: string;
  imageCaption?: string;
  showImage?: boolean;
  // DEV-NOTE: 'full' option not needed in FAQ compontent
  imageSize?: 'small' | 'medium' | 'large';
  apiKey: string;
  apiVersion: string;
  targetId: string;
  requestUrlPositive: string;
  requestUrlNegative: string;
  requestUrlFeedback: string;
}

@Component({
  tag: 'ui-cms-faq-entry',
  styleUrl: 'ui-cms-faq-entry.scss',
  shadow: false,
})
export class UiCmsFaqEntry {
  @Element() el: HTMLUiCmsFaqEntryElement;

  @Prop({ attribute: 'body' }) utilityBody: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Prop({ mutable: true }) body: UiCmsFaqEntryBody = {} as any;

  /**
   * When the entry is part of a dropdown, the question should be hidden
   * as it is already shown in the dropdown button.
   */
  @Prop() hideQuestion = false;

  /**
   * The category of which the entry is part of. Currently only used for tracking.
   */
  @Prop() categoryHeadline: string;

  @Prop({ mutable: true }) i18n = {
    thisFAQ: 'diese FAQ',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
  }

  render() {
    return (
      <Host>
        {this.body.question && !this.hideQuestion && (
          <h1 class="sd-headline sd-headline--size-4xl">{this.body.question}</h1>
        )}
        <div class="ui-cms-faq-entry__answer">
          <ui-cms-text-media
            class="ui-cms-faq-entry__answer--long"
            body={{
              mediaType: this.body.showImage ? 'image' : '',
              image: {
                src: this.body.image,
                alt: this.body.imageAltText,
                mouseover: this.body.imageMouseover,
                caption: this.body.imageCaption,
                copyright: this.body.imageCopyright,
              },
              mediaSize: this.body.imageSize,
              richtext: this.body.answer,
            }}
          ></ui-cms-text-media>
        </div>
        {this.body.links ? (
          <ui-cms-link-list
            body={{
              links: this.body.links.map((link: CmsLink) => ({
                ...link,
                delay: DEFAULT_TRACKING_DELAY,
              })),
              columns: 1,
            }}
          ></ui-cms-link-list>
        ) : (
          ''
        )}
        <ui-cms-rating
          body={{
            requestUrl: this.body.requestUrl,
            itemId: this.body.faqId,
            apiKey: this.body.apiKey,
            apiVersion: this.body.apiVersion,
            targetId: this.body.targetId,
            requestUrlPositive: this.body.requestUrlPositive,
            requestUrlNegative: this.body.requestUrlNegative,
            countItemLabel: this.i18n.thisFAQ,
            feedbackItemLabel: this.i18n.thisFAQ,
          }}
          requestUrlFeedback={this.body.requestUrlFeedback}
          faq={this.body.question}
          url={this.body.url ?? (window.location.href || 'missing url')}
          additionalTrackingContent={this.body.question}
          withFeedbackModal
        ></ui-cms-rating>
      </Host>
    );
  }
}
